import { useLogger } from '~/composables/useLogger';
import { isClient } from '~/utils';

export const AGENT_EXCLUSIVES_CAROUSEL_STORAGE_PREFIX =
	'agentExclusivesCarouselIndex';

const logger = useLogger('agentExclusivesCarouselPosition');

function getStorageKey(agentSlug: string): string {
	return `${AGENT_EXCLUSIVES_CAROUSEL_STORAGE_PREFIX}:${agentSlug}`;
}

/**
 * Envolve todo acesso ao `sessionStorage`.
 *
 * Ele **lança** — em vez de devolver null — quando o navegador bloqueia dados
 * do site: janelas privadas de alguns Safaris, iframes, configurações
 * restritivas. Sem a guarda, um visitante nesse estado veria a seção quebrar;
 * com ela, o carrossel apenas volta a abrir no primeiro item.
 */
function readWriteSafely<T>(
	operation: 'read' | 'persist' | 'clear',
	fallback: T,
	run: () => T
): T {
	if (!isClient()) return fallback;

	try {
		return run();
	} catch (error) {
		logger.error('Agent exclusives carousel sessionStorage operation failed', {
			operation,
			error
		});

		return fallback;
	}
}

/**
 * Guarda o slide atual para que voltar da PDP, da SRP ou do feed de shorts
 * devolva o visitante ao mesmo ponto do carrossel. A chave inclui o corretor
 * porque perfis diferentes têm carrosséis diferentes.
 *
 * Gravar só na saída para um desses três destinos é a primeira metade do que
 * separa "voltar" de "entrar": a seção não persiste cada troca de slide, senão
 * uma entrada nova no perfil pela home, na mesma aba, também reabriria o
 * carrossel no meio. A outra metade é `isReturnNavigation`, na leitura.
 */
export function saveAgentExclusivesCarouselIndex(
	agentSlug: string,
	index: number
): void {
	if (!agentSlug || !Number.isInteger(index) || index < 0) return;

	readWriteSafely('persist', undefined, () =>
		sessionStorage.setItem(getStorageKey(agentSlug), String(index))
	);
}

/**
 * Segunda metade da distinção entre "voltar" e "entrar".
 *
 * Gravar apenas na saída para os três destinos não fecha o caso: quem sai para
 * a SRP, passa pela home e entra no perfil de novo, na mesma aba, ainda
 * encontraria o carrossel no meio sem ter voltado de lugar nenhum.
 *
 * A entrada de histórico do perfil só ganha `forward` depois que o visitante
 * avança dela — e, ao voltar, é essa mesma entrada que volta a ser a atual.
 * Numa entrada nova o campo vem vazio, então a posição guardada é descartada
 * em vez de aplicada.
 */
export function isReturnNavigation(): boolean {
	if (!isClient()) return false;

	const state = window.history.state as { forward?: string | null } | null;

	return Boolean(state?.forward);
}

export function getAgentExclusivesCarouselIndex(agentSlug: string): number {
	if (!agentSlug) return 0;

	const stored = readWriteSafely('read', null, () =>
		sessionStorage.getItem(getStorageKey(agentSlug))
	);

	const parsed = Number(stored);

	return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
}

/**
 * Descarta a posição depois de usá-la. Sem isso ela sobreviveria à volta e
 * qualquer entrada posterior no perfil, na mesma aba, cairia no meio do
 * carrossel em vez de começar no primeiro imóvel.
 */
export function clearAgentExclusivesCarouselIndex(agentSlug: string): void {
	if (!agentSlug) return;

	readWriteSafely('clear', undefined, () =>
		sessionStorage.removeItem(getStorageKey(agentSlug))
	);
}
